import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { CalendarClock, ChevronLeft, ChevronRight, Clock3, Palmtree, Plus } from "lucide-react";
import { useAuth } from "@/app/AuthContext";
import { useToast } from "@/app/Toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/data";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Shift {
  id: number;
  source: "TEMPLATE" | "OVERRIDE";
  localDate: string;
  startAt: string;
  endAt: string;
  scheduledMinutes: number;
  breaks: Array<{ startAt: string; endAt: string; paid: boolean }>;
}

interface Leave {
  id: number;
  startAt: string;
  endAt: string;
  type: string;
  reason: string | null;
  status: "APPROVED" | "PENDING" | "REJECTED" | "CANCELLED";
}

interface StaffSchedule {
  staffUserId: number;
  resourceId: number | null;
  name: string;
  timezone: string;
  shifts: Shift[];
  leave: Leave[];
  scheduledMinutes: number;
  actualMinutes: number;
  overtimeMinutes: number;
}

interface ScheduleResponse {
  locationId: number;
  timezone: string;
  from: string;
  to: string;
  overtimeRule: { dailyThresholdMinutes: number | null; weeklyThresholdMinutes: number | null; multiplier: number } | null;
  staff: StaffSchedule[];
}

const inputClass = "h-10 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-primary/25";

export function WorkforcePage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const canManage = user?.role === "ADMIN" || user?.role === "MANAGER";
  const defaultLocation = user?.memberships[0]?.locationId ?? 0;
  const [locationId, setLocationId] = useState(defaultLocation);
  const [weekStart, setWeekStart] = useState(() => DateTime.local().startOf("week").toISODate()!);
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShift, setShowShift] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ start: "", end: "", type: "Vacation", reason: "" });
  const [shiftForm, setShiftForm] = useState({ staffUserId: "", dayOfWeek: "1", startTime: "09:00", endTime: "17:00", breakStart: "12:00", breakEnd: "12:30", breakPaid: false });

  const weekEnd = DateTime.fromISO(weekStart).plus({ days: 6 }).toISODate()!;
  const days = Array.from({ length: 7 }, (_, index) => DateTime.fromISO(weekStart).plus({ days: index }));

  async function load() {
    if (!locationId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await api.get<ScheduleResponse>(`/api/v1/workforce/schedule?locationId=${locationId}&from=${weekStart}&to=${weekEnd}`);
      setSchedule(data);
      if (!shiftForm.staffUserId && data.staff[0]) setShiftForm((current) => ({ ...current, staffUserId: String(data.staff[0]!.staffUserId) }));
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Could not load workforce schedule");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [locationId, weekStart]);

  async function requestLeave(event: React.FormEvent) {
    event.preventDefault();
    if (!schedule) return;
    try {
      const start = DateTime.fromISO(leaveForm.start, { zone: schedule.timezone });
      const end = DateTime.fromISO(leaveForm.end, { zone: schedule.timezone });
      await api.post("/api/v1/workforce/leave", {
        locationId, startAt: start.toUTC().toISO(), endAt: end.toUTC().toISO(),
        leaveType: leaveForm.type, reason: leaveForm.reason || null,
      });
      toast("success", "Leave request submitted");
      setLeaveForm({ start: "", end: "", type: "Vacation", reason: "" });
      await load();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Could not request leave");
    }
  }

  async function createTemplate(event: React.FormEvent) {
    event.preventDefault();
    try {
      await api.post("/api/v1/workforce/shift-templates", {
        locationId, staffUserId: Number(shiftForm.staffUserId), dayOfWeek: Number(shiftForm.dayOfWeek), sequence: 0,
        startTime: shiftForm.startTime, endTime: shiftForm.endTime, effectiveFrom: weekStart,
        breaks: shiftForm.breakStart && shiftForm.breakEnd ? [{ startTime: shiftForm.breakStart, endTime: shiftForm.breakEnd, paid: shiftForm.breakPaid }] : [],
      });
      toast("success", "Weekly shift added");
      setShowShift(false);
      await load();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Could not add shift");
    }
  }

  async function decideLeave(id: number, status: "APPROVED" | "REJECTED") {
    try {
      await api.patch(`/api/v1/workforce/leave/${id}/status`, { status });
      toast("success", `Leave ${status.toLowerCase()}`);
      await load();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Could not update leave");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-primary"><CalendarClock className="h-4 w-4" /> Workforce</div>
          <h1 className="text-2xl font-semibold tracking-tight">Week schedule</h1>
          <p className="mt-1 text-sm text-muted-foreground">Shifts, breaks, leave and worked time in {schedule?.timezone ?? "the location timezone"}.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === "ADMIN" && <input aria-label="Location ID" type="number" min="1" className={cn(inputClass, "w-28")} value={locationId || ""} onChange={(event) => setLocationId(Number(event.target.value))} />}
          <div className="flex items-center rounded-lg border bg-white p-1 shadow-sm">
            <Button variant="ghost" size="sm" aria-label="Previous week" onClick={() => setWeekStart(DateTime.fromISO(weekStart).minus({ weeks: 1 }).toISODate()!)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-40 px-2 text-center text-sm font-semibold">{DateTime.fromISO(weekStart).toFormat("d LLL")} - {DateTime.fromISO(weekEnd).toFormat("d LLL yyyy")}</span>
            <Button variant="ghost" size="sm" aria-label="Next week" onClick={() => setWeekStart(DateTime.fromISO(weekStart).plus({ weeks: 1 }).toISODate()!)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          {canManage && <Button onClick={() => setShowShift((value) => !value)}><Plus className="h-4 w-4" /> Add shift</Button>}
        </div>
      </div>

      {showShift && canManage && (
        <Card><CardHeader><CardTitle className="text-base">New recurring weekly shift</CardTitle></CardHeader><CardContent>
          <form className="grid gap-3 md:grid-cols-4 xl:grid-cols-8" onSubmit={createTemplate}>
            <label className="text-xs font-semibold text-muted-foreground md:col-span-2">Staff<select className={cn(inputClass, "mt-1")} value={shiftForm.staffUserId} onChange={(e) => setShiftForm({ ...shiftForm, staffUserId: e.target.value })}>{schedule?.staff.map((staff) => <option key={staff.staffUserId} value={staff.staffUserId}>{staff.name}</option>)}</select></label>
            <label className="text-xs font-semibold text-muted-foreground">Day<select className={cn(inputClass, "mt-1")} value={shiftForm.dayOfWeek} onChange={(e) => setShiftForm({ ...shiftForm, dayOfWeek: e.target.value })}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => <option key={day} value={index}>{day}</option>)}</select></label>
            <Field label="Start" type="time" value={shiftForm.startTime} onChange={(value) => setShiftForm({ ...shiftForm, startTime: value })} />
            <Field label="End" type="time" value={shiftForm.endTime} onChange={(value) => setShiftForm({ ...shiftForm, endTime: value })} />
            <Field label="Break start" type="time" value={shiftForm.breakStart} onChange={(value) => setShiftForm({ ...shiftForm, breakStart: value })} />
            <Field label="Break end" type="time" value={shiftForm.breakEnd} onChange={(value) => setShiftForm({ ...shiftForm, breakEnd: value })} />
            <div className="flex items-end gap-2"><label className="mb-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={shiftForm.breakPaid} onChange={(e) => setShiftForm({ ...shiftForm, breakPaid: e.target.checked })} /> Paid</label><Button className="ml-auto" type="submit">Save</Button></div>
          </form>
        </CardContent></Card>
      )}

      <div className="hidden overflow-x-auto rounded-xl border bg-white shadow-sm lg:block">
        <div className="grid min-w-[1050px] grid-cols-[190px_repeat(7,minmax(120px,1fr))]">
          <div className="border-b border-r bg-muted/40 p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Team member</div>
          {days.map((day) => <div key={day.toISODate()} className="border-b border-r bg-muted/40 p-4 last:border-r-0"><div className="text-xs font-bold uppercase text-muted-foreground">{day.toFormat("ccc")}</div><div className="mt-1 text-lg font-semibold">{day.toFormat("d")}</div></div>)}
          {schedule?.staff.map((staff) => <ScheduleRow key={staff.staffUserId} staff={staff} days={days} timezone={schedule.timezone} />)}
        </div>
      </div>

      <div className="space-y-3 lg:hidden">
        {schedule?.staff.map((staff) => <Card key={staff.staffUserId}><CardHeader><CardTitle className="flex items-center justify-between text-base"><span>{staff.name}</span><span className="text-xs font-normal text-muted-foreground">{formatMinutes(staff.scheduledMinutes)} scheduled</span></CardTitle></CardHeader><CardContent className="space-y-2">{days.map((day) => <DayCell key={day.toISODate()} staff={staff} day={day} timezone={schedule.timezone} mobile />)}</CardContent></Card>)}
      </div>

      {loading && <div className="rounded-xl border bg-white p-10 text-center text-sm text-muted-foreground">Loading schedule...</div>}
      {!loading && schedule?.staff.length === 0 && <div className="rounded-xl border bg-white p-10 text-center text-sm text-muted-foreground">No staff are assigned to this location.</div>}

      <div className="grid gap-4 xl:grid-cols-[1fr_1.35fr]">
        <Card><CardHeader><CardTitle className="flex items-center gap-2 text-base"><Palmtree className="h-4 w-4 text-primary" /> Request leave</CardTitle></CardHeader><CardContent>
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={requestLeave}>
            <Field label="Starts" type="datetime-local" value={leaveForm.start} onChange={(value) => setLeaveForm({ ...leaveForm, start: value })} />
            <Field label="Ends" type="datetime-local" value={leaveForm.end} onChange={(value) => setLeaveForm({ ...leaveForm, end: value })} />
            <Field label="Type" value={leaveForm.type} onChange={(value) => setLeaveForm({ ...leaveForm, type: value })} />
            <Field label="Reason" value={leaveForm.reason} onChange={(value) => setLeaveForm({ ...leaveForm, reason: value })} />
            <Button type="submit" className="sm:col-span-2">Submit request</Button>
          </form>
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="flex items-center gap-2 text-base"><Clock3 className="h-4 w-4 text-primary" /> Weekly totals</CardTitle></CardHeader><CardContent className="space-y-3">
          {schedule?.staff.map((staff) => <div key={staff.staffUserId} className="grid grid-cols-[1fr_repeat(3,auto)] items-center gap-4 rounded-lg border p-3 text-sm"><span className="font-semibold">{staff.name}</span><Metric label="Scheduled" value={formatMinutes(staff.scheduledMinutes)} /><Metric label="Actual" value={formatMinutes(staff.actualMinutes)} /><Metric label="Overtime" value={formatMinutes(staff.overtimeMinutes)} accent={staff.overtimeMinutes > 0} /></div>)}
          {canManage && schedule?.staff.flatMap((staff) => staff.leave.filter((leave) => leave.status === "PENDING").map((leave) => <div key={leave.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm"><span className="font-semibold">{staff.name}</span><span className="text-muted-foreground">{leave.type}, {DateTime.fromISO(leave.startAt).setZone(schedule.timezone).toFormat("d LLL HH:mm")}</span><Button className="ml-auto" size="sm" onClick={() => void decideLeave(leave.id, "APPROVED")}>Approve</Button><Button variant="outline" size="sm" onClick={() => void decideLeave(leave.id, "REJECTED")}>Reject</Button></div>))}
        </CardContent></Card>
      </div>
    </div>
  );
}

function ScheduleRow({ staff, days, timezone }: { staff: StaffSchedule; days: DateTime[]; timezone: string }) {
  return <><div className="border-b border-r p-4"><div className="font-semibold">{staff.name}</div><div className="mt-1 text-xs text-muted-foreground">{formatMinutes(staff.scheduledMinutes)} / {formatMinutes(staff.actualMinutes)} actual</div>{staff.overtimeMinutes > 0 && <Badge className="mt-2">{formatMinutes(staff.overtimeMinutes)} OT</Badge>}</div>{days.map((day) => <DayCell key={day.toISODate()} staff={staff} day={day} timezone={timezone} />)}</>;
}

function DayCell({ staff, day, timezone, mobile = false }: { staff: StaffSchedule; day: DateTime; timezone: string; mobile?: boolean }) {
  const date = day.toISODate();
  const shifts = staff.shifts.filter((shift) => shift.localDate === date);
  const leave = staff.leave.filter((item) => DateTime.fromISO(item.startAt).setZone(timezone).toISODate() === date && ["APPROVED", "PENDING"].includes(item.status));
  return <div className={cn("min-h-28 border-b border-r p-2.5 last:border-r-0", mobile && "min-h-0 rounded-lg border p-3")}>
    {mobile && <div className="mb-2 text-xs font-bold uppercase text-muted-foreground">{day.toFormat("ccc, d LLL")}</div>}
    {shifts.map((shift) => <div key={`${shift.source}-${shift.id}-${shift.startAt}`} className="mb-2 rounded-lg border border-sky-200 bg-sky-50 p-2 text-xs text-sky-950"><div className="font-bold">{DateTime.fromISO(shift.startAt).setZone(timezone).toFormat("HH:mm")} - {DateTime.fromISO(shift.endAt).setZone(timezone).toFormat("HH:mm")}</div>{shift.breaks.map((pause) => <div key={pause.startAt} className="mt-1 text-sky-700">Break {DateTime.fromISO(pause.startAt).setZone(timezone).toFormat("HH:mm")} {pause.paid ? "paid" : "unpaid"}</div>)}</div>)}
    {leave.map((item) => <div key={item.id} className={cn("rounded-lg p-2 text-xs font-semibold", item.status === "APPROVED" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700")}>{item.type}<div className="mt-0.5 font-normal">{item.status.toLowerCase()}</div></div>)}
    {!shifts.length && !leave.length && <span className="text-xs text-muted-foreground">Off</span>}
  </div>;
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return <label className="text-xs font-semibold text-muted-foreground">{label}<input required className={cn(inputClass, "mt-1")} type={type} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function Metric({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return <span className="text-right"><span className="block text-[10px] uppercase text-muted-foreground">{label}</span><span className={cn("font-semibold", accent && "text-amber-700")}>{value}</span></span>;
}

function formatMinutes(minutes: number): string {
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export default WorkforcePage;
